import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "relative w-full" }
const _hoisted_2 = ["accept"]
const _hoisted_3 = { class: "text-[#2f6fab] flex flex-col items-center select-none pointer-events-none group-hover:scale-105 transition-transform" }
const _hoisted_4 = { class: "text-xs font-black text-[#173b5f]" }
const _hoisted_5 = { class: "text-[10px] text-[#748197] mt-1" }
const _hoisted_6 = ["src"]
const _hoisted_7 = {
  key: 1,
  class: "absolute inset-0 grid grid-cols-3 grid-rows-3 pointer-events-none opacity-30"
}
const _hoisted_8 = {
  key: 2,
  class: "absolute top-2.5 left-2.5 pointer-events-none bg-slate-900/75 backdrop-blur-xs text-[9px] font-bold text-white px-2.5 py-0.5 rounded-full border border-white/20 flex items-center gap-1.5 shadow-xs"
}
const _hoisted_9 = {
  key: 0,
  class: "absolute top-2.5 right-2.5 flex items-center gap-1.5 z-10"
}
const _hoisted_10 = {
  key: 0,
  class: "bg-slate-900/80 text-white text-[9.5px] px-2 py-0.5 rounded-md font-semibold border border-white/20"
}

import { ref, computed, watch, onMounted, onUnmounted } from "vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'InlineImageCanvasCropper',
  props: {
    modelValue: {},
    existingUrl: { default: "" },
    aspectRatio: { default: 1 },
    isBanner: { type: Boolean, default: false },
    height: { default: "" },
    label: { default: "Select Image" },
    hint: { default: "PNG, JPG, WEBP" },
    accept: { default: ".png,.jpg,.jpeg,.webp" },
    maxSizeMB: { default: 5 },
    targetWidth: { default: 800 },
    targetHeight: { default: 800 },
    fileName: { default: "cropped-image.png" }
  },
  emits: ["update:modelValue", "change"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const fileInputRef = ref<HTMLInputElement | null>(null);
const viewportRef = ref<HTMLDivElement | null>(null);
const imageRef = ref<HTMLImageElement | null>(null);

const rawSrc = ref("");
const isEditing = ref(false);
const isExporting = ref(false);
const imageLoaded = ref(false);

const naturalWidth = ref(0);
const naturalHeight = ref(0);

// Crop Viewport Dimensions (measured in pixels)
const viewportWidth = ref(200);
const viewportHeight = ref(112);

// User Transforms
const userZoom = ref(1.0);
const offsetX = ref(0);
const offsetY = ref(0);

// Drag State
const isDragging = ref(false);
const dragStart = { x: 0, y: 0, initialOffsetX: 0, initialOffsetY: 0 };

const hasImage = computed(() => Boolean(rawSrc.value || props.modelValue || props.existingUrl));

// Style for Viewport Boundary
const viewportStyle = computed(() => {
  if (props.isBanner) {
    return {
      width: "100%",
      height: "100%",
      borderRadius: "1.5rem",
    };
  }
  // Square Logo: centered 132x132px frame
  return {
    width: "132px",
    height: "132px",
    borderRadius: "0.5rem",
  };
});

// Base scale needed so image completely fills the viewport
const baseScale = computed(() => {
  if (!naturalWidth.value || !naturalHeight.value || !viewportWidth.value || !viewportHeight.value) {
    return 1;
  }
  return Math.max(
    viewportWidth.value / naturalWidth.value,
    viewportHeight.value / naturalHeight.value
  );
});

const currentScale = computed(() => baseScale.value * userZoom.value);
const renderedWidth = computed(() => naturalWidth.value * currentScale.value);
const renderedHeight = computed(() => naturalHeight.value * currentScale.value);

function clampOffsets(x: number, y: number) {
  const maxOffsetX = Math.max(0, (renderedWidth.value - viewportWidth.value) / 2);
  const maxOffsetY = Math.max(0, (renderedHeight.value - viewportHeight.value) / 2);
  return {
    x: Math.max(-maxOffsetX, Math.min(maxOffsetX, x)),
    y: Math.max(-maxOffsetY, Math.min(maxOffsetY, y)),
  };
}

const imageTransformStyle = computed(() => {
  const cx = viewportWidth.value / 2;
  const cy = viewportHeight.value / 2;
  const left = cx - renderedWidth.value / 2 + offsetX.value;
  const top = cy - renderedHeight.value / 2 + offsetY.value;

  return {
    width: `${renderedWidth.value}px`,
    height: `${renderedHeight.value}px`,
    left: `${left}px`,
    top: `${top}px`,
  };
});

function measureViewport() {
  if (!viewportRef.value) return;
  viewportWidth.value = viewportRef.value.clientWidth || (props.isBanner ? 380 : 132);
  viewportHeight.value = viewportRef.value.clientHeight || (props.isBanner ? 380 : 132);

  const clamped = clampOffsets(offsetX.value, offsetY.value);
  offsetX.value = clamped.x;
  offsetY.value = clamped.y;
}

function triggerUpload() {
  fileInputRef.value?.click();
}

function handleFileChange(e: Event) {
  const target = e.target as HTMLInputElement;
  if (target.files?.[0]) {
    const file = target.files[0];
    if (file.size > props.maxSizeMB * 1024 * 1024) {
      alert(`File exceeds the ${props.maxSizeMB}MB size limit.`);
      target.value = "";
      return;
    }

    if (rawSrc.value && rawSrc.value.startsWith("blob:")) {
      URL.revokeObjectURL(rawSrc.value);
    }

    rawSrc.value = URL.createObjectURL(file);
    imageLoaded.value = false;
    userZoom.value = 1.0;
    offsetX.value = 0;
    offsetY.value = 0;
    isEditing.value = true; // Automatically enters edit mode on upload!

    target.value = "";
  }
}

function onImageLoaded(e: Event) {
  const img = e.target as HTMLImageElement;
  naturalWidth.value = img.naturalWidth;
  naturalHeight.value = img.naturalHeight;
  imageLoaded.value = true;
  measureViewport();
  scheduleAutoSave();
}

function startEditing() {
  measureViewport();
}

function zoomStep(delta: number) {
  userZoom.value = Math.max(1.0, Math.min(3.5, +(userZoom.value + delta).toFixed(2)));
  const clamped = clampOffsets(offsetX.value, offsetY.value);
  offsetX.value = clamped.x;
  offsetY.value = clamped.y;
}

function onWheel(e: WheelEvent) {
  if (!imageLoaded.value) return;
  const delta = e.deltaY < 0 ? 0.08 : -0.08;
  zoomStep(delta);
  scheduleAutoSave();
}

// Mouse dragging
function onMouseDown(e: MouseEvent) {
  if (e.button !== 0 || !imageLoaded.value) return;
  isDragging.value = true;
  dragStart.x = e.clientX;
  dragStart.y = e.clientY;
  dragStart.initialOffsetX = offsetX.value;
  dragStart.initialOffsetY = offsetY.value;

  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseup", onMouseUp);
}

function onMouseMove(e: MouseEvent) {
  if (!isDragging.value) return;
  const dx = e.clientX - dragStart.x;
  const dy = e.clientY - dragStart.y;
  const clamped = clampOffsets(dragStart.initialOffsetX + dx, dragStart.initialOffsetY + dy);
  offsetX.value = clamped.x;
  offsetY.value = clamped.y;
}

function onMouseUp() {
  if (isDragging.value) {
    isDragging.value = false;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
    scheduleAutoSave();
  }
}

// Touch dragging
function onTouchStart(e: TouchEvent) {
  if (e.touches.length !== 1 || !imageLoaded.value) return;
  isDragging.value = true;
  dragStart.x = e.touches[0].clientX;
  dragStart.y = e.touches[0].clientY;
  dragStart.initialOffsetX = offsetX.value;
  dragStart.initialOffsetY = offsetY.value;

  window.addEventListener("touchmove", onTouchMove);
  window.addEventListener("touchend", onTouchEnd);
}

function onTouchMove(e: TouchEvent) {
  if (!isDragging.value || e.touches.length !== 1) return;
  const dx = e.touches[0].clientX - dragStart.x;
  const dy = e.touches[0].clientY - dragStart.y;
  const clamped = clampOffsets(dragStart.initialOffsetX + dx, dragStart.initialOffsetY + dy);
  offsetX.value = clamped.x;
  offsetY.value = clamped.y;
}

function onTouchEnd() {
  if (isDragging.value) {
    isDragging.value = false;
    window.removeEventListener("touchmove", onTouchMove);
    window.removeEventListener("touchend", onTouchEnd);
    scheduleAutoSave();
  }
}

watch(userZoom, () => {
  const clamped = clampOffsets(offsetX.value, offsetY.value);
  offsetX.value = clamped.x;
  offsetY.value = clamped.y;
});

let autoSaveTimeout: ReturnType<typeof setTimeout> | null = null;

function scheduleAutoSave() {
  if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
  autoSaveTimeout = setTimeout(() => {
    applyCrop();
  }, 180);
}

// Render high-res crop on offscreen canvas and emit result
async function applyCrop() {
  if (!imageRef.value || !imageLoaded.value) return;

  isExporting.value = true;
  try {
    const scale = currentScale.value;
    const cx = viewportWidth.value / 2;
    const cy = viewportHeight.value / 2;

    const imgLeft = cx - renderedWidth.value / 2 + offsetX.value;
    const imgTop = cy - renderedHeight.value / 2 + offsetY.value;

    const sx = Math.max(0, -imgLeft / scale);
    const sy = Math.max(0, -imgTop / scale);
    const sWidth = Math.min(naturalWidth.value - sx, viewportWidth.value / scale);
    const sHeight = Math.min(naturalHeight.value - sy, viewportHeight.value / scale);

    const canvas = document.createElement("canvas");
    canvas.width = props.targetWidth;
    canvas.height = props.targetHeight;
    const ctx = canvas.getContext("2d");

    if (!ctx) throw new Error("Canvas context failed");

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      imageRef.value,
      sx,
      sy,
      sWidth,
      sHeight,
      0,
      0,
      props.targetWidth,
      props.targetHeight
    );

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/png", 0.95);
    });

    if (blob) {
      const file = new File([blob], props.fileName, { type: "image/png" });
      emit("update:modelValue", file);
      emit("change", file);
    }
  } catch (err) {
    console.error("Failed to crop canvas image:", err);
  } finally {
    isExporting.value = false;
  }
}

function removeImage() {
  if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
  if (rawSrc.value && rawSrc.value.startsWith("blob:")) {
    URL.revokeObjectURL(rawSrc.value);
  }
  rawSrc.value = "";
  imageLoaded.value = false;
  isEditing.value = false;
  userZoom.value = 1.0;
  offsetX.value = 0;
  offsetY.value = 0;
  emit("update:modelValue", null);
  emit("change", null);
}

// Initialize from existingUrl if provided
watch(
  () => props.existingUrl,
  (newUrl) => {
    if (newUrl && !rawSrc.value && !props.modelValue) {
      rawSrc.value = newUrl;
    }
  },
  { immediate: true }
);

onMounted(() => {
  window.addEventListener("resize", measureViewport);
  setTimeout(measureViewport, 60);
});

onUnmounted(() => {
  if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
  window.removeEventListener("resize", measureViewport);
  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("mouseup", onMouseUp);
  window.removeEventListener("touchmove", onTouchMove);
  window.removeEventListener("touchend", onTouchEnd);
  if (rawSrc.value && rawSrc.value.startsWith("blob:")) {
    URL.revokeObjectURL(rawSrc.value);
  }
});

__expose({
  triggerUpload,
  removeImage,
  applyCrop,
  hasImage,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("input", {
      ref_key: "fileInputRef",
      ref: fileInputRef,
      type: "file",
      class: "hidden",
      accept: __props.accept,
      onChange: handleFileChange
    }, null, 40, _hoisted_2),
    (!hasImage.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["border-2 border-dashed border-[#c1cfe1] hover:border-[#2f6fab] bg-[#f8fbff] hover:bg-[#f2f7fd] rounded-[1.5rem] p-6 text-center cursor-pointer transition-all flex flex-col items-center justify-center w-full group shadow-2xs", __props.isBanner ? 'aspect-square' : 'min-h-[148px]']),
          onClick: triggerUpload
        }, [
          _createElementVNode("div", _hoisted_3, [
            _cache[0] || (_cache[0] = _createElementVNode("div", { class: "w-12 h-12 rounded-full bg-[#eaf3fb] flex items-center justify-center mb-2.5 shadow-2xs" }, [
              _createElementVNode("svg", {
                class: "w-6 h-6 text-[#2855a6]",
                fill: "none",
                stroke: "currentColor",
                "stroke-width": "2",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  d: "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                })
              ])
            ], -1)),
            _createElementVNode("span", _hoisted_4, _toDisplayString(__props.label), 1),
            _createElementVNode("span", _hoisted_5, _toDisplayString(__props.hint), 1)
          ])
        ], 2))
      : (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: _normalizeClass(["relative border border-[#d8e4f0] bg-[#0c121e] rounded-[1.5rem] overflow-hidden flex items-center justify-center transition-all w-full", __props.isBanner ? 'aspect-square' : '']),
          style: _normalizeStyle(__props.isBanner ? {} : { minHeight: props.height || '148px' })
        }, [
          _createElementVNode("div", {
            ref_key: "viewportRef",
            ref: viewportRef,
            class: _normalizeClass(["relative w-full h-full overflow-hidden flex items-center justify-center select-none touch-none rounded-[1.5rem]", isDragging.value ? 'cursor-grabbing' : 'cursor-grab']),
            style: _normalizeStyle(viewportStyle.value),
            onMousedown: onMouseDown,
            onTouchstartPassive: onTouchStart,
            onWheel: _withModifiers(onWheel, ["prevent"])
          }, [
            (rawSrc.value)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 0,
                  ref_key: "imageRef",
                  ref: imageRef,
                  src: rawSrc.value,
                  alt: "Canvas Image",
                  crossorigin: "anonymous",
                  class: "absolute max-w-none pointer-events-none transition-transform duration-75 ease-out origin-center",
                  style: _normalizeStyle(imageTransformStyle.value),
                  onLoad: onImageLoaded
                }, null, 44, _hoisted_6))
              : _createCommentVNode("", true),
            (isDragging.value && imageLoaded.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [...(_cache[1] || (_cache[1] = [
                  _createStaticVNode("<div class=\"border-r border-b border-white\"></div><div class=\"border-r border-b border-white\"></div><div class=\"border-b border-white\"></div><div class=\"border-r border-b border-white\"></div><div class=\"border-r border-b border-white\"></div><div class=\"border-b border-white\"></div><div class=\"border-r border-b border-white\"></div><div class=\"border-r border-b border-white\"></div><div></div>", 9)
                ]))]))
              : _createCommentVNode("", true),
            (imageLoaded.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [...(_cache[2] || (_cache[2] = [
                  _createElementVNode("span", { class: "w-1.5 h-1.5 rounded-full bg-[#38bdf8] animate-pulse" }, null, -1),
                  _createElementVNode("span", null, "Drag to Pan • Ctrl+Scroll to Zoom", -1)
                ]))]))
              : _createCommentVNode("", true)
          ], 38),
          (hasImage.value && imageLoaded.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                (isExporting.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_10, " Saving... "))
                  : _createCommentVNode("", true),
                _createElementVNode("button", {
                  type: "button",
                  class: "inline-flex items-center gap-1 bg-white/95 hover:bg-white text-[#173b5f] px-2 py-1 rounded-md text-[11px] font-bold shadow-md transition-all active:scale-95 cursor-pointer",
                  title: "Change Image",
                  onClick: _withModifiers(triggerUpload, ["stop"])
                }, [...(_cache[3] || (_cache[3] = [
                  _createElementVNode("svg", {
                    class: "w-3.5 h-3.5 text-[#2855a6]",
                    fill: "none",
                    stroke: "currentColor",
                    "stroke-width": "2",
                    viewBox: "0 0 24 24"
                  }, [
                    _createElementVNode("path", {
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round",
                      d: "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                    })
                  ], -1),
                  _createElementVNode("span", null, "Change", -1)
                ]))]),
                _createElementVNode("button", {
                  type: "button",
                  class: "bg-[#be243d] hover:bg-[#9c182d] text-white p-1 rounded-md text-[11px] shadow-md transition-all active:scale-95 cursor-pointer",
                  title: "Remove Image",
                  onClick: _withModifiers(removeImage, ["stop"])
                }, [...(_cache[4] || (_cache[4] = [
                  _createElementVNode("svg", {
                    class: "w-3.5 h-3.5",
                    fill: "none",
                    stroke: "currentColor",
                    "stroke-width": "2",
                    viewBox: "0 0 24 24"
                  }, [
                    _createElementVNode("path", {
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round",
                      d: "M6 18L18 6M6 6l12 12"
                    })
                  ], -1)
                ]))])
              ]))
            : _createCommentVNode("", true)
        ], 6))
  ]))
}
}

})